/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */

/**
 * Constants for Guard access
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.0.0
 */

import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';

import { IAccess } from '../../types/guardTypes';
import { FooterButtonsAccess } from './footerButtonsConstants';
import FooterAccess from './footerGuardConstants';
import { AccessComponentKeyEnum, EGuardAuthUserFlag, FeatureNameEnum } from './guardConstants';
import PageAccess from './pageGuardConstants';
import TabMenuAccess from './tabMenuGuardConstants';

/**
 * Configuration for access to certain features and components
 * @type {{PageHistoryOrder: string[]; TabMenu: string[]}}
 */
export const Access: IAccess = {
    [AccessComponentKeyEnum.columnResize]: {
        featureName: FeatureNameEnum.COLUMN_RESIZE,
    },
    [AccessComponentKeyEnum.Campaigns]: {},
    [AccessComponentKeyEnum.CampaignsAlternative]: {},
    [AccessComponentKeyEnum.DensityListView]: {},
    [AccessComponentKeyEnum.endUserDeleteBulk]: {
        featureName: FeatureNameEnum.END_USER_DELETE_BULK,
    },
    [AccessComponentKeyEnum.inquiries]: {
        featureName: FeatureNameEnum.INQUIRIES,
        featureVersion: [3, 4],
    },
    [AccessComponentKeyEnum.otherInquiries]: {
        featureName: FeatureNameEnum.OTHER_INQUIRIES,
    },
    [AccessComponentKeyEnum.LabelsImportButton]: {
        authUserData: {
            isEnabled: false,
            name: EGuardAuthUserFlag.IS_SUBSCRIBER,
        },
        role: [
               AuthorityRoleEnum.ROLE_COORDINATOR_USER,
               AuthorityRoleEnum.ROLE_TAGS_IMPORT,
        ],
    },
    [AccessComponentKeyEnum.multiBalances]: {
        featureName: FeatureNameEnum.MULTI_BALANCES,
    },
    [AccessComponentKeyEnum.onboarding]: {
        authUserData: {
            isEnabled: false,
            name: EGuardAuthUserFlag.IS_SUBSCRIBER,
        },
        featureName: FeatureNameEnum.ONBOARDING,
    },
    [AccessComponentKeyEnum.personalAccountDisablingWarning]: {
        featureName: FeatureNameEnum.PERSONAL_ACCOUNT_DISABLE_WARNING,
    },
    [AccessComponentKeyEnum.personalDataConfirmCheck]: {
        featureName: FeatureNameEnum.PERSONAL_DATA_CONFIRM_CHECK,
    },
    [AccessComponentKeyEnum.Settings]: {},
    [AccessComponentKeyEnum.surveyMedallia]: {
        featureName: FeatureNameEnum.SURVEY_MEDALLIA,
    },
    [AccessComponentKeyEnum.wizardChangeLockHideCost]: {
        featureName: FeatureNameEnum.WIZARD_CHANGE_LOCK_HIDE_COST,
    },
    [AccessComponentKeyEnum.reportsConstructor]: {
        featureName: FeatureNameEnum.REPORTS_CONSTRUCTOR,
    },
    [AccessComponentKeyEnum.megaPowers]: {
        featureName: FeatureNameEnum.MEGA_POWERS,
    },
    [AccessComponentKeyEnum.notifications]: {
        authUserData: {
            isEnabled: false,
            name: EGuardAuthUserFlag.IS_SUBSCRIBER,
        },
    },

    // PAGES
    ...PageAccess,
    // TAB MENU
    ...TabMenuAccess,
    // FOOTER
    ...FooterAccess,
    // FOOTER BUTTONS
    ...FooterButtonsAccess,
};
